import { action } from '@ember/object';
import Service, { type Registry as Services, service } from '@ember/service';
import type { Dayjs } from 'dayjs';
import type { HotelAndMarketServiceWithMarket } from 'lumen/services/hotel-and-market';
import {
  REFERENCE_PERIOD_TRANSLATIONS_SHORT,
  VIEWS,
} from 'lumen/utils/constants/total-revenue-constants';
import safeDivide from 'lumen/utils/numeric/safe-divide';

export default class TotalRevenueService extends Service {
  @service('currency') declare currencyService: Services['currency'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithMarket;
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get currency() {
    return (
      this.currencyService.selectedCurrency ??
      this.hotelAndMarketService.market.revenueInsightCurrency
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isPerAvailableRoom() {
    const isPerAvailableRoomQP =
      this.router.currentRoute?.queryParams['isPerAvailableRoom'];

    // Per available room doesn't make sense in the strategy view
    const viewQP = this.router.currentRoute?.queryParams['view'];
    if (viewQP === VIEWS.STRATEGY) {
      return false;
    }

    return isPerAvailableRoomQP === 'true';
  }

  calculateChange(value: number, referenceValue: number): number;
  calculateChange(
    value: number | null,
    referenceValue: number | null,
  ): number | null;
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  calculateChange(value: number | null, referenceValue: number | null) {
    if (value === null || referenceValue === null) {
      return null;
    }

    return value - referenceValue;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  calculatePAR(value: number, capacity: number | undefined) {
    return safeDivide(value, capacity ?? 0);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  sortReferenceValues<T extends { referenceFromDate: Dayjs }>(
    referenceValues: T[],
  ) {
    return referenceValues.sort((a, b) =>
      a.referenceFromDate.isBefore(b.referenceFromDate) ? 1 : -1,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  translateReferenceType(referenceType: string) {
    const translationKey =
      REFERENCE_PERIOD_TRANSLATIONS_SHORT[
        referenceType as keyof typeof REFERENCE_PERIOD_TRANSLATIONS_SHORT
      ];

    return translationKey ? this.intl.t(translationKey) : referenceType;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  valueOrPAR(value: number, capacity: number | undefined) {
    return this.isPerAvailableRoom ? this.calculatePAR(value, capacity) : value;
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  calculateNonRoomPremium(nonRoomRevenue: number, roomRevenue: number) {
    const division = safeDivide(nonRoomRevenue, roomRevenue);
    return division !== null ? division * 100 : null;
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  calculateRelativeChange(
    value: number | null | undefined,
    referenceValue: number | null | undefined,
  ) {
    if (
      value === null ||
      value === undefined ||
      referenceValue === null ||
      referenceValue === undefined ||
      value < 0 ||
      referenceValue < 0
    ) {
      return null;
    }

    if (value < 0 || referenceValue < 0) {
      return null;
    }

    const absoluteChange = value - referenceValue;
    return safeDivide(absoluteChange, referenceValue);
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  formatChange(change: number | null) {
    if (change === null) {
      return this.intl.t('general.missing');
    }

    // Always show the sign except when the change is a true zero.
    // e.g. +0.0001 => +0%, -0.0001 => -0% and 0 => 0%
    const signDisplay = change === 0 ? 'never' : 'always';

    return this.intl.formatNumber(change, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      signDisplay,
    });
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  formatPercentage(value: number | null) {
    if (value === null) {
      return this.intl.t('general.missing');
    }

    return this.intl.formatNumber(value, {
      style: 'percent',
    });
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  formatRelativeChange(change: number | null) {
    if (change === null) {
      return this.intl.t('general.missing');
    }

    // Always show the sign except when the change is a true zero.
    // e.g. +0.0001 => +0%, -0.0001 => -0% and 0 => 0%
    const signDisplay = change === 0 ? 'never' : 'always';

    return this.intl.formatNumber(change, {
      style: 'percent',
      signDisplay,
    });
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  formatValue(value: number | null | undefined) {
    if (value === null || value === undefined) {
      return this.intl.t('general.missing');
    }

    return this.intl.formatNumber(value, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }
}

declare module '@ember/service' {
  interface Registry {
    'total-revenue': TotalRevenueService;
  }
}
